import { useCallback, useEffect, useRef, useState } from "react";
import { API, apiFetch } from "../../../pages/admin/_shared";
import { sanitizeFileName } from "./mediaUtils";
import { SINGLE_UPLOAD_MAX_BYTES, buildUniqueUploadKey, guessUploadContentType } from "../../../lib/multipartUpload";

// Upload 1 request bị BE giới hạn ~4.5MB → tệp lớn hơn đi multipart presigned.
const LARGE = SINGLE_UPLOAD_MAX_BYTES;
const PART = 10 * 1024 * 1024;
const CONCURRENCY = 2;

const norm = (p) => (p ? (String(p).endsWith("/") ? String(p) : `${p}/`) : "");

function xhrPut(url, blob, onProgress, reg) {
  return new Promise((resolve, reject) => {
    const xhr = new XMLHttpRequest();
    reg?.(xhr);
    xhr.open("PUT", url, true);
    xhr.upload.onprogress = (e) => onProgress?.(e.loaded || 0);
    xhr.onload = () =>
      xhr.status >= 200 && xhr.status < 300
        ? resolve({ etag: xhr.getResponseHeader("ETag") || xhr.getResponseHeader("etag") || "" })
        : reject(new Error(`Upload thất bại (${xhr.status})`));
    xhr.onerror = () => reject(new Error("Lỗi mạng khi tải lên"));
    xhr.onabort = () => reject(Object.assign(new Error("Đã huỷ"), { aborted: true }));
    xhr.send(blob);
  });
}

function xhrForm(url, fd, token, onProgress, reg) {
  return new Promise((resolve, reject) => {
    const xhr = new XMLHttpRequest();
    reg?.(xhr);
    xhr.open("POST", url, true);
    if (token) xhr.setRequestHeader("Authorization", `Bearer ${token}`);
    xhr.upload.onprogress = (e) => onProgress?.(e.loaded || 0);
    xhr.onload = () => {
      let json = {};
      try {
        json = xhr.responseText ? JSON.parse(xhr.responseText) : {};
      } catch {
        /* không phải JSON */
      }
      xhr.status >= 200 && xhr.status < 300 ? resolve(json) : reject(new Error(json?.message || `Upload thất bại (${xhr.status})`));
    };
    xhr.onerror = () => reject(new Error("Lỗi mạng khi tải lên"));
    xhr.onabort = () => reject(Object.assign(new Error("Đã huỷ"), { aborted: true }));
    xhr.send(fd);
  });
}

/**
 * Hàng đợi tải lên nhiều tệp: tiến trình từng tệp, thử lại khi lỗi, huỷ, tự chạy tối đa 2 tệp cùng lúc.
 * Tệp > 4MB dùng multipart presigned trực tiếp lên S3 (key duy nhất, không ghi đè).
 */
export default function useUploadQueue({ onFinished }) {
  const [jobs, setJobs] = useState([]); // {id,name,size,prefix,status:queued|uploading|done|error|canceled,loaded,error}
  const filesRef = useRef(new Map()); // id -> File
  const xhrRef = useRef(new Map()); // id -> Set<XHR>
  const runningRef = useRef(0);
  const onFinishedRef = useRef(onFinished);
  onFinishedRef.current = onFinished;

  const patch = useCallback((id, p) => setJobs((all) => all.map((j) => (j.id === id ? { ...j, ...p } : j))), []);

  const run = useCallback(
    async (job) => {
      const file = filesRef.current.get(job.id);
      if (!file) return;
      const reg = (x) => {
        const set = xhrRef.current.get(job.id) || new Set();
        set.add(x);
        xhrRef.current.set(job.id, set);
      };
      const name = sanitizeFileName(file.name);
      try {
        if (file.size > LARGE) {
          // Key duy nhất mỗi lần upload (tránh ghi đè tệp trùng tên); key đã tồn tại (409 KEY_EXISTS) → tạo key mới, thử lại 1 lần
          let key = buildUniqueUploadKey(norm(job.prefix), name, file.type);
          const parts = Math.ceil(file.size / PART);
          const createUpload = (k) =>
            apiFetch(`${API}/media/multipart/create`, {
              method: "POST",
              body: JSON.stringify({ key: k, contentType: guessUploadContentType(file), cacheControl: "public, max-age=0, must-revalidate" }),
            });
          let created;
          try {
            created = await createUpload(key);
          } catch (e) {
            if (e?.code !== "KEY_EXISTS" && Number(e?.status) !== 409) throw e;
            key = buildUniqueUploadKey(norm(job.prefix), name, file.type);
            created = await createUpload(key);
          }
          if (created?.data?.key) key = String(created.data.key);
          const uploadId = created?.data?.uploadId;
          if (!uploadId) throw new Error("Không nhận được uploadId");
          const etags = new Array(parts);
          const loaded = new Array(parts).fill(0);
          const sync = () => patch(job.id, { loaded: loaded.reduce((a, b) => a + b, 0) });
          let next = 0;
          try {
            const worker = async () => {
              while (next < parts) {
                const i = next++;
                const blob = file.slice(i * PART, Math.min(file.size, (i + 1) * PART));
                const signed = await apiFetch(`${API}/media/multipart/sign-part`, { method: "POST", body: JSON.stringify({ key, uploadId, partNumber: i + 1 }) });
                const { etag } = await xhrPut(
                  signed?.data?.url,
                  blob,
                  (l) => {
                    loaded[i] = Math.min(blob.size, l);
                    sync();
                  },
                  reg
                );
                if (!etag) throw new Error("Thiếu ETag");
                etags[i] = { ETag: etag, PartNumber: i + 1 };
                loaded[i] = blob.size;
                sync();
              }
            };
            await Promise.all(Array.from({ length: Math.min(3, parts) }, worker));
            await apiFetch(`${API}/media/multipart/complete`, { method: "POST", body: JSON.stringify({ key, uploadId, parts: etags }) });
          } catch (e) {
            apiFetch(`${API}/media/multipart/abort`, { method: "POST", body: JSON.stringify({ key, uploadId }) }).catch(() => {});
            throw e;
          }
        } else {
          const fd = new FormData();
          fd.append("file", file);
          const token = localStorage.getItem("authToken") || localStorage.getItem("adminToken") || "";
          await xhrForm(`${API}/media/upload?prefix=${encodeURIComponent(job.prefix)}`, fd, token, (l) => patch(job.id, { loaded: l }), reg);
        }
        patch(job.id, { status: "done", loaded: file.size });
        filesRef.current.delete(job.id);
      } catch (e) {
        patch(job.id, e.aborted ? { status: "canceled", error: "Đã huỷ" } : { status: "error", error: e.message || "Tải lên thất bại" });
      } finally {
        xhrRef.current.delete(job.id);
      }
    },
    [patch]
  );

  // Bộ điều phối: luôn giữ tối đa CONCURRENCY tệp đang chạy.
  useEffect(() => {
    const queued = jobs.filter((j) => j.status === "queued");
    const free = CONCURRENCY - runningRef.current;
    if (free > 0 && queued.length) {
      queued.slice(0, free).forEach((job) => {
        runningRef.current += 1;
        patch(job.id, { status: "uploading", error: "" });
        run(job).finally(() => {
          runningRef.current -= 1;
          setJobs((all) => [...all]); // đánh thức bộ điều phối
        });
      });
    }
    const active = jobs.some((j) => j.status === "queued" || j.status === "uploading");
    if (!active && jobs.some((j) => j.status === "done" && !j.reported)) {
      setJobs((all) => all.map((j) => (j.status === "done" ? { ...j, reported: true } : j)));
      onFinishedRef.current?.(jobs.filter((j) => j.status === "done" && !j.reported).length);
    }
  }, [jobs, run, patch]);

  const enqueue = useCallback((files, prefix) => {
    const added = Array.from(files).map((file) => {
      const id = `${Date.now()}-${Math.random().toString(36).slice(2, 8)}`;
      filesRef.current.set(id, file);
      return { id, name: sanitizeFileName(file.name), size: file.size, prefix, status: "queued", loaded: 0, error: "" };
    });
    setJobs((all) => [...all, ...added]);
  }, []);

  const retry = useCallback((id) => patch(id, { status: "queued", loaded: 0, error: "" }), [patch]);
  const cancel = useCallback(
    (id) => {
      const set = xhrRef.current.get(id);
      if (set) set.forEach((x) => x.abort());
      else patch(id, { status: "canceled", error: "Đã huỷ" });
    },
    [patch]
  );
  const clearFinished = useCallback(() => {
    setJobs((all) => all.filter((j) => j.status === "queued" || j.status === "uploading"));
  }, []);

  return { jobs, enqueue, retry, cancel, clearFinished };
}
